import { useEffect } from 'react';
import { useTheme } from 'next-themes';
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useRouter } from 'next/router';
import { BuilderComponent, builder } from '@builder.io/react';
import {
  PlasmicComponent,
  PlasmicRootProvider,
  extractPlasmicQueryData
} from '@plasmicapp/loader-nextjs';
import { PLASMIC } from 'react-scale/lib/plasmic/init';

import { NextSeo } from 'next-seo';
import 'react-scale/components/builder';
import 'react-scale/components/builder-settings';

const BUILDER_API_KEY = process.env.NEXT_PUBLIC_BUILDER_API_KEY;
builder.init(BUILDER_API_KEY);

type BuilderPage = {
  data: {
    title: string;
    description: string;
    url: string;
    ogImg: string;
    twImg: string;
    noIndex: boolean;
    theme: string;
    autoGenerateThumbnail: {
      enabled: boolean;
      title: string;
      subtitle: string;
    };
  };
};

export async function getStaticProps({ params }: GetStaticPropsContext) {
  if (!Array.isArray(params.page)) return { notFound: true };

  // 1. Prioritize PLASMIC data over builder legacy pages
  const plasmicData = await PLASMIC.maybeFetchComponentData(`/${params.page.join('/')}`);

  if (!plasmicData) {
    // 2. Fetch Builder as a fallback
    const page: BuilderPage | null =
      (await builder
        .get('page', {
          userAttributes: {
            urlPath: `/${params?.page?.join('/') || ''}`
          },
          limit: 200
        })
        .toPromise()) || null;

    // 3. If there is no builder page either, return null
    if (!page) {
      return {
        notFound: true,
        revalidate: 60
      };
    }

    return {
      props: {
        page
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 5 seconds
      revalidate: 1
    };
  }

  // This is a path that Plasmic knows about.
  const pageMeta = plasmicData.entryCompMetas[0];

  // Cache the necessary data fetched for the page.
  const queryCache = await extractPlasmicQueryData(
    <PlasmicRootProvider loader={PLASMIC} prefetchedData={plasmicData} pageParams={pageMeta.params}>
      <PlasmicComponent component={pageMeta.displayName} />
    </PlasmicRootProvider>
  );

  return {
    props: { plasmicData, queryCache },
    revalidate: 60
  };
}

export async function getStaticPaths() {
  const builderPages = await builder.getAll('page', {
    options: { noTargeting: true },
    omit: 'data.blocks',
    limit: 200
  });
  const plasmicPages = await PLASMIC.fetchPages();

  return {
    paths: [
      ...builderPages.map(page => `${page.data?.url}`),
      ...plasmicPages.map(page => ({
        params: { page: page.path.substring(1).split('/') }
      }))
    ],
    fallback: 'blocking'
  };
}

export default function Page({
  page,
  plasmicData,
  queryCache
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter();
  if (router.isFallback) {
    return <h1>Loading...</h1>;
  }

  if (!page) {
    const pageMeta = plasmicData.entryCompMetas[0];
    return (
      // Pass in the data fetched in getStaticProps as prefetchedData
      <PlasmicRootProvider
        loader={PLASMIC}
        prefetchedData={plasmicData}
        prefetchedQueryData={queryCache}
        pageParams={pageMeta.params}
        pageQuery={router.query}
      >
        {
          // pageMeta.displayName contains the name of the component you fetched.
        }
        <PlasmicComponent component={pageMeta.displayName} />
      </PlasmicRootProvider>
    );
  }

  const { title, description, url, noIndex, ogImg, autoGenerateThumbnail } = page?.data! || {};

  const { setTheme } = useTheme();

  useEffect(() => {
    setTheme(page?.data.theme ?? 'dark');
  }, []);

  const og = page?.data?.autoGenerateThumbnail?.enabled
    ? `https://scale.com/api/og/guides?title=${encodeURIComponent(
        autoGenerateThumbnail?.title
      )}&subtitle=${encodeURIComponent(autoGenerateThumbnail?.subtitle)}`
    : ogImg;

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={`https://scale.com${url}`}
        noindex={noIndex}
        openGraph={{
          images: [{ url: og, width: 1200, height: 630, alt: 'Og Image Alt', type: 'image/jpeg' }]
        }}
      />

      <BuilderComponent model="page" content={page} options={{ includeRefs: true }} />
    </>
  );
}
